// useAppRoutes.js
import { useMemo,useEffect,useContext,useState } from "react";
import MDBox from "components/MDBox";
import { publicRoutes } from "../routes";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import { Navigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import ProfileTabs from "layouts/profile/components/profile/ProfileTabs";
// import Billing from "layouts/billing";
import Billing from "layouts/billing/components/billing/BillingPage"
import APITokenPage from "layouts/apiToken/components/apiToken/APITokenPage";
import RecoveryTokenPage from "layouts/2fa/components/2fa/RecoveryTokenPage"
import ManageGAPage  from "layouts/2fa/components/2fa/ManageGAPage"
import StrategySummaryPage from "layouts/strategy/components/strategy/StrategySummaryPage";
import StrategyManagerPage from "layouts/strategy/components/strategy/StrategyManagerPage";
import PlanUpgradePage from "layouts/plan/components/plan/PlanUpgradePage";
import CoinUpgradePage from "layouts/plan/components/plan/CoinUpgradePage";
import InvoicesPage from "layouts/billing/components/billing/InvoicesPage";
import StrategyMarketPage from "layouts/strategy/components/strategy/StrategyMarketPage";
import ReferralTabs from "layouts/referral/components/referral/ReferralTabs";
import StrategyAiPage from "layouts/strategy/components/strategy/StrategyAiPage";
import Beast from "layouts/beast";
// import BeastStrategyFormPage from "layouts/strategy/components/strategy/BeastStrategyFormPage";
import AdminDashboard from "layouts/admin/dashboard";
import UserManager from "layouts/admin/users";
import SystemSettings from "layouts/admin/settings";
import AccountingReport from "layouts/admin/accounting";
import ReferralWithdrawalsAdmin from "layouts/admin/referral-withdrawals";
import PromoCodesAdmin from "layouts/admin/promo-codes";
import UsageAnalytics from "layouts/admin/analytics";
import OpsAnalytics from "layouts/admin/ops-analytics";
import ExeSessions from "layouts/admin/exe-sessions";
import RegimeConsole from "layouts/admin/regime-console";
import AdminLogin from "layouts/authentication/admin-login";
import BankFactoryPage from "layouts/admin/bank-factory";
import BankElitePage from "layouts/admin/bank-elite";
import BankSkillPage from "layouts/admin/bank-skills";
import BankPitfallPage from "layouts/admin/bank-pitfalls";
import BankIncubatorPage from "layouts/admin/bank-incubator";
import AdminNotifications from "layouts/admin/notifications";
import SupportCenterAdmin from "layouts/admin/support";
import PulseCenterPage from "layouts/pulse-center";
import MASGenie from "layouts/mas-genie";
import MyGenieStrategies from "layouts/mas-genie/MyGenieStrategies";
import DiscordLinked from "layouts/discord/DiscordLinked";
import Icon from "@mui/material/Icon";
import { useAuth } from "../AuthContext";

// 側欄 admin 項目 menu key → 功能 key（與後端 config/adminFeatures.js 一致）。
// STAFF 只顯示 adminFeatures 內功能；ADMIN 全顯示。
const MENU_KEY_TO_FEATURE = {
  "admin-dashboard": "dashboard",
  "admin-users": "users",
  "admin-analytics": "analytics",
  "admin-ops-analytics": "ops-analytics",
  "admin-exe-sessions": "exe-sessions",
  "admin-regime-console": "regime-console",
  "admin-settings": "settings",
  "admin-accounting": "accounting",
  "admin-referral-withdrawals": "referral-withdrawals",
  "admin-promo-codes": "promo-codes",
  "admin-support": "support",
  "admin-notifications": "notifications",
  "admin-bank-factory": "bank-factory",
  "admin-bank-elite": "bank-factory",
  "admin-bank-skills": "bank-factory",
  "admin-bank-pitfalls": "bank-factory",
  "admin-bank-incubator": "bank-incubator",
  "admin-pulse-center": "pulse-center",
};

export default function useAppRoutes(isAuthenticated, isInitializing = false) {
const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("routes", "routes/routes_lang");
  }, [language, loadLanguageFile]);
  const LANGUAGE = useMemo(() => componentLanguageData.routes || { Main: {} }, [componentLanguageData]);
const isLangReady = !!LANGUAGE?.Main;

  const { user, token } = useAuth();
  const [visibleMenuKeys, setVisibleMenuKeys] = useState(null);
  // 後台功能權限（role + adminFeatures）：決定 admin 側欄顯示哪些項目
  const [adminAccess, setAdminAccess] = useState(null);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetch("/api/users/admin-access", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => res.json())
        .then((data) => setAdminAccess(data))
        .catch(() => setAdminAccess({ role: user?.role || "USER", adminFeatures: [] }));
    }
  }, [isAuthenticated, token, user]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetch("/api/users/visible-menus", {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => setVisibleMenuKeys(data))
        .catch(() => setVisibleMenuKeys([])); // 發生錯誤時預設為空，或可改為預設全開
    }
  }, [isAuthenticated, token]);

  //console.log("📦 useAppRoutes 呼叫：", { isAuthenticated, isInitializing, visibleMenuKeys });
  return useMemo(() => {
    if (isInitializing) return [];
    if (!isLangReady || isInitializing) return [];



    const privateRoutes = [
      // 2026-05-21 MAS 精靈拿掉下拉、直接做為 leaf；icon 換成 Mas_genie.png
      // 「我的精靈策略」改成 hidden 同層 route（保留路由可用、但不在 sidebar 顯示）
      // 注意：key 必須保持 "mas-genie-group" 才會出現在後端 visibleMenuKeys 白名單（[useAppRoutes.js:173]）
      {
        type: "collapse",
        name: LANGUAGE.Main.Content32 || "策略精靈",
        key: "mas-genie-group",
        icon: <MDBox component="img" src="/images/menu/Mas_genie.png" width="30px" height="30px" />,
        route: "/mas-genie",
        // 在「我的精靈策略」頁時也要 highlight 此項
        matchPaths: ["/my-genie-strategies"],
        component: <MASGenie />,
      },
      {
        type: "collapse",
        name: LANGUAGE.Main.Content34 || "我的精靈策略",
        key: "my-genie-strategies",
        route: "/my-genie-strategies",
        component: <MyGenieStrategies />,
        hidden: true,
      },
      // Discord 綁定導回頁（隱藏；不進側欄。對方 OAuth 完成後 302 導回此頁）
      {
        type: "collapse",
        name: "Discord",
        key: "discord-linked",
        route: "/discord/linked",
        component: <DiscordLinked />,
        hidden: true,
      },
      {
        type: "collapse",
        name: LANGUAGE.Main.Content30 || "策略助理",
        key: "strategy-group",
        icon: <MDBox component="img" src="/images/menu/Artificial Intelligence.png" width="30px" height="30px" />,
        children: [
          { type: "collapse", name: LANGUAGE.Main.Content1, key: "strategy-summary", route: "/strategy-summary", component: <StrategySummaryPage /> },
          { type: "collapse", name: LANGUAGE.Main.Content2, key: "strategy-manager", route: "/strategy-manager", component: <StrategyManagerPage /> },
          // 2026-05-25 策略市集暫時隱藏（依 Sean 要求）— route 還在，直接打網址仍可進
          { type: "collapse", name: LANGUAGE.Main.Content12, key: "strategy-market", route: "/strategy-market", component: <StrategyMarketPage />, hidden: true },
        ]
      },
      {
        type: "collapse",
        name: LANGUAGE.Main.Content5,
        key: "user-group",
        icon: <MDBox component="img" src="/images/menu/Frame 81.png" width="30px" height="30px" />,
        children: [
          { type: "collapse", name: LANGUAGE.Main.Content5, key: "profile", route: "/profile/*", component: <ProfileTabs /> },
          { type: "collapse", name: LANGUAGE.Main.Content13, key: "referral", route: "/referral/*", component: <ReferralTabs /> },
          { type: "collapse", name: LANGUAGE.Main.Content3, key: "PlanUpgradePage", route: "/plans", component: <PlanUpgradePage /> },
          { type: "collapse", name: LANGUAGE.Main.Content4, key: "CoinUpgradePage", route: "/coins", component: <CoinUpgradePage /> },
          { type: "collapse", name: LANGUAGE.Main.Content6, key: "billing", route: "/billing", component: <Billing /> },
        ]
      },
      {
        type: "collapse",
        name: LANGUAGE.Main.Content31 || "其他設定",
        key: "settings-group",
        icon: <MDBox component="img" src="/images/menu/Frame 80.png" width="30px" height="30px" />,
        children: [
          { type: "collapse", name: LANGUAGE.Main.Content8, key: "api-token", route: "/api-token", component: <APITokenPage />, hidden: true },
          { type: "collapse", name: LANGUAGE.Main.Content10, key: "ga-manage", route: "/2fa/manage-ga", component: <ManageGAPage /> },
          { type: "collapse", name: LANGUAGE.Main.Content9, key: "recovery-tokens", route: "/2fa/tokens", component: <RecoveryTokenPage /> },
          { type: "collapse", name: LANGUAGE.Main.Content11, key: "error-return", route: "https://mindaismart.com/error_return", external: true, component: <ManageGAPage /> },
        ]
      },
    ];


    // ADMIN 顯示全部；STAFF 只顯示被授權的功能（adminFeatures）。ADMIN 行為維持原樣。
    const isAdminUser = user?.role === "ADMIN" && user?.isAdminSession === true;
    const isStaffUser = adminAccess?.role === "STAFF";
    if (isAdminUser || isStaffUser) {
      const adminItems = [
        { type: "collapse", name: LANGUAGE.Main.Content100 || "管理者總覽", key: "admin-dashboard",  icon: <Icon fontSize="small">admin_panel_settings</Icon>, route: "/admin/dashboard",      component: <AdminDashboard /> },
        { type: "collapse", name: LANGUAGE.Main.Content101 || "用戶管理", key: "admin-users",      icon: <Icon fontSize="small">people</Icon>,                route: "/admin/users",         component: <UserManager /> },
        { type: "collapse", name: "使用熱力分析", key: "admin-analytics", icon: <Icon fontSize="small">insights</Icon>, route: "/admin/analytics", component: <UsageAnalytics /> },
        { type: "collapse", name: "營運分析", key: "admin-ops-analytics", icon: <Icon fontSize="small">query_stats</Icon>, route: "/admin/ops-analytics", component: <OpsAnalytics /> },
        { type: "collapse", name: "EXE 使用狀況", key: "admin-exe-sessions", icon: <Icon fontSize="small">desktop_windows</Icon>, route: "/admin/exe-sessions", component: <ExeSessions /> },
        { type: "collapse", name: "趨勢引擎判定台", key: "admin-regime-console", icon: <Icon fontSize="small">timeline</Icon>, route: "/admin/regime-console", component: <RegimeConsole /> },
        { type: "collapse", name: LANGUAGE.Main.Content102 || "系統設定", key: "admin-settings",   icon: <Icon fontSize="small">display_settings</Icon>,     route: "/admin/settings",      component: <SystemSettings /> },
        { type: "collapse", name: LANGUAGE.Main.Content103 || "財務統計", key: "admin-accounting", icon: <Icon fontSize="small">account_balance</Icon>,      route: "/admin/accounting",    component: <AccountingReport /> },
        { type: "collapse", name: "推薦提領審核", key: "admin-referral-withdrawals", icon: <Icon fontSize="small">payments</Icon>, route: "/admin/referral-withdrawals", component: <ReferralWithdrawalsAdmin /> },
        { type: "collapse", name: "優惠碼管理", key: "admin-promo-codes", icon: <Icon fontSize="small">confirmation_number</Icon>, route: "/admin/promo-codes", component: <PromoCodesAdmin /> },
        { type: "collapse", name: "客服中心 (Crisp)", key: "admin-support", icon: <Icon fontSize="small">support_agent</Icon>, route: "/admin/support", component: <SupportCenterAdmin /> },
        { type: "collapse", name: LANGUAGE.Main.Content104 || "通知管理", key: "admin-notifications", icon: <Icon fontSize="small">notifications_active</Icon>, route: "/admin/notifications", component: <AdminNotifications /> },
        { type: "collapse", name: "策略銀行工廠",             key: "admin-bank-factory", icon: <Icon fontSize="small">factory</Icon>,              route: "/admin/bank-factory",  component: <BankFactoryPage /> },
        { type: "collapse", name: "Beast 精英策略庫",         key: "admin-bank-elite",   icon: <Icon fontSize="small">auto_awesome</Icon>,      route: "/admin/bank-elite",    component: <BankElitePage /> },
        { type: "collapse", name: "🎖️ 宗師技能館",             key: "admin-bank-skills",  icon: <Icon fontSize="small">menu_book</Icon>,         route: "/admin/bank-skills",   component: <BankSkillPage /> },
        { type: "collapse", name: "🚨 避坑指南",               key: "admin-bank-pitfalls", icon: <Icon fontSize="small">report_problem</Icon>,   route: "/admin/bank-pitfalls", component: <BankPitfallPage /> },
        { type: "collapse", name: "🌱 策略孵化池",              key: "admin-bank-incubator", icon: <Icon fontSize="small">eco</Icon>,               route: "/admin/bank-incubator", component: <BankIncubatorPage /> },
        { type: "collapse", name: "⚡ Beast AI 脈衝中心",       key: "admin-pulse-center", icon: <Icon fontSize="small">bolt</Icon>,              route: "/admin/pulse-center",  component: <PulseCenterPage /> },
      ];
      const granted = adminAccess?.adminFeatures || [];
      const visibleAdminItems = isAdminUser
        ? adminItems
        : adminItems.filter((it) => granted.includes(MENU_KEY_TO_FEATURE[it.key]));
      if (visibleAdminItems.length > 0) {
        privateRoutes.push(
          { type: "divider", key: "admin-divider" },
          { type: "label", title: "管理者專欄", key: "admin-label" },
          ...visibleAdminItems
        );
      }
    }

    // Beast AI route - accessible to everyone (no login required)
    const beastRoute = { 
      type: "collapse", 
      name: "Beast AI", 
      key: "beast-ai", 
      icon: <Icon fontSize="small">psychology</Icon>, 
      route: "/beast", 
      component: <Beast /> 
    };

    // const hiddenPrivateRoutes = [
    //   {
    //     name: "Beast AI Strategy (Hidden)",
    //     key: "beast-strategy",
    //     route: "/beast-strategy",
    //     component: <PrivateRoute><BeastStrategyFormPage /></PrivateRoute>,
    //     hidden: true, // 👈 可供未來 UI 過濾使用
    //   },
    // ];

    // --- 動態過濾邏輯 ---
    const filterRoutesByVisibility = (routesList) => {
      if (!visibleMenuKeys) return routesList; // 還沒載入完則先顯示（或可改為不顯示）
      
      return routesList.map(route => {
        // 如果這個 Key 不在可見清單中，強制隱藏
        let isHidden = route.hidden || false;
        if (!visibleMenuKeys.includes(route.key) && !route.key.startsWith("admin-")) {
          isHidden = true;
        }

        const newRoute = { ...route, hidden: isHidden };

        // 如果有子項目，也遞迴過濾
        if (newRoute.children) {
          newRoute.children = filterRoutesByVisibility(newRoute.children);
          
          // 如果所有子項目都被隱藏了，那父項目也隱藏 (可選邏輯)
          const hasVisibleChild = newRoute.children.some(c => !c.hidden);
          if (!hasVisibleChild && !newRoute.key.startsWith("admin-")) {
            newRoute.hidden = true;
          }
        }

        return newRoute;
      });
    };

    const filteredPrivateRoutes = filterRoutesByVisibility(privateRoutes);

    if (isAuthenticated) {
      return [
        {
          route: "/",
          component: <Navigate to="/mas-genie" />,
        },
        ...publicRoutes.map((r) => ({
          ...r,
          component: <PublicRoute>{r.component}</PublicRoute>,
        })),
        {
          name: "Admin Login",
          key: "admin-login",
          route: "/admin/login",
          component: <AdminLogin />,
          hidden: true,
        },
        // Beast AI - accessible without PrivateRoute wrapper
        // beastRoute,
        ...filteredPrivateRoutes.map((r) => ({
          ...r,
          component: <PrivateRoute>{r.component}</PrivateRoute>,
        })),
        // ...hiddenPrivateRoutes,
      ];
    } else {
      return [
        {
          route: "/",
          component: <Navigate to="/authentication/sign-in" />,
        },
        ...publicRoutes.map((r) => ({
          ...r,
          component: <PublicRoute>{r.component}</PublicRoute>,
        })),
        {
          name: "Admin Login",
          key: "admin-login",
          route: "/admin/login",
          component: <AdminLogin />,
          hidden: true,
        },
        // Beast AI - accessible without login
        beastRoute,
      ];
    }
  },  [isAuthenticated, isInitializing, isLangReady, LANGUAGE, user, publicRoutes, visibleMenuKeys, adminAccess]);
}
